import { get } from '@ember/helper';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import {
  PrismBadge,
  PrismButton,
  PrismCard,
  PrismTooltip,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { Illustration } from 'frontend-utils';
import type { CentralMappingBreakdown } from 'lumen/models';
import {
  BREAKDOWN_DESCRIPTIONS,
  BREAKDOWN_LOGOS,
  BREAKDOWN_TRANSLATIONS,
  BREAKDOWNS,
  CHANNEL_ROOM_MAPPING_ROUTE,
} from 'lumen/utils/central-mapping-constants';

interface SettingsCentralMappingMenuBreakdownCardSignature {
  Args: {
    breakdown: CentralMappingBreakdown;
    hierarchyRoute: string;
    mappingRoute: string;
  };
}

export default class SettingsCentralMappingMenuBreakdownCard extends Component<SettingsCentralMappingMenuBreakdownCardSignature> {
  @service declare router: Services['router'];
  @service declare usageAnalytics: Services['usage-analytics'];

  get showHierarchyButton(): boolean {
    return Boolean(
      this.args.breakdown.breakdown !== BREAKDOWNS.CHANNEL_ROOM_CATEGORIES &&
      this.args.breakdown.breakdown !== BREAKDOWNS.BENCHMARK_MARKET_SEGMENT,
    );
  }

  get showMenuCardBadge(): boolean {
    return this.args.breakdown.totalValuesUnmapped > 0;
  }

  @action onClickNavigateToHierarchy(): void {
    const { hierarchyRoute, breakdown } = this.args;

    this.router.transitionTo(hierarchyRoute, {
      queryParams: { breakdown: breakdown.breakdown },
    });

    this.usageAnalytics.track('Hierarchy Page Opened', {
      eventTrigger: 'manage-hierarchy',
      type: breakdown.breakdown.split('_').join(' '),
    });
  }

  @action onClickNavigateToMapping(): void {
    const { mappingRoute, breakdown } = this.args;

    if (breakdown.breakdown === BREAKDOWNS.CHANNEL_ROOM_CATEGORIES) {
      this.usageAnalytics.track('Channel Room Mapping Viewed', {
        eventTrigger: 'settings-mapping',
      });
    }

    let route = mappingRoute;

    if (breakdown.breakdown === BREAKDOWNS.CHANNEL_ROOM_CATEGORIES) {
      route = CHANNEL_ROOM_MAPPING_ROUTE;
    } else if (breakdown.breakdown === BREAKDOWNS.BENCHMARK_MARKET_SEGMENT) {
      route = 'hotel.settings.mapping.benchmark';
    }

    this.router.transitionTo(route, {
      queryParams: { breakdown: breakdown.breakdown },
    });

    this.usageAnalytics.track('Mapping Page Opened', {
      eventTrigger: 'manage-mapping',
      type: breakdown.breakdown.split('_').join(' '),
    });
  }

  @action onClickRedirectToRatePlan(): void {
    this.router.transitionTo(
      'hotel.settings.spider.spider-dashboard',
      'rate-plans',
    );
  }

  <template>
    <PrismCard
      @contentLayout="noPadding"
      class="central-mapping-menu-card"
      data-breakdown={{@breakdown.breakdown}}
      data-test-id="central-mapping-menu-card"
    >
      <:content>
        <div class="m-400">
          <div class="flex flex-row justify-between">
            <Illustration
              @html={{get BREAKDOWN_LOGOS @breakdown.breakdown}}
              aria-label={{t "settings.central_mapping.menu_card.alt"}}
            />

            {{#if this.showMenuCardBadge}}
              <PrismBadge @iconName="connection" @type="warning">
                <div data-test-id="central-mapping-menu-card-badge">
                  {{@breakdown.totalValuesUnmapped}}
                </div>

                <PrismTooltip
                  @text={{t "settings.central_mapping.menu.badge.tooltip"}}
                />
              </PrismBadge>
            {{/if}}
          </div>

          <h3 class="mt-300" data-test-id="central-mapping-menu-card-title">{{t
              (get BREAKDOWN_TRANSLATIONS @breakdown.breakdown)
            }}</h3>

          <p
            class="text-300--regular mt-300"
            data-test-id="central-mapping-menu-card-description"
          >{{t (get BREAKDOWN_DESCRIPTIONS @breakdown.breakdown)}}</p>
        </div>
      </:content>

      <:footer>
        <div
          class="flex gap-200
            {{if this.showHierarchyButton 'justify-between' 'justify-end'}}"
        >
          {{#if this.showHierarchyButton}}
            <PrismButton
              @buttonType="ghost"
              @label={{t "settings.central-mapping.menu.hierarchy.view"}}
              @onClick={{this.onClickNavigateToHierarchy}}
              data-test-id="central-mapping-menu-hierarchy-navigation-button"
            />
          {{/if}}
          <PrismButton
            @buttonType="secondary"
            @icon="arrow-right"
            @label={{t "settings.central-mapping.mapping"}}
            @onClick={{this.onClickNavigateToMapping}}
            data-test-id="central-mapping-menu-mapping-navigation-button"
          />
        </div>
      </:footer>
    </PrismCard>
  </template>
}
